import { useNavigate } from 'react-router';
import { useState, type ReactNode } from 'react';
import {
  Check,
  Copy,
  Download,
  Info,
  Loader2,
  MessageCircle,
  Pause,
  RefreshCw,
  ScrollText,
  TriangleAlert,
  X,
} from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DangerZone } from '@/components/danger-zone';
import { DisconnectedChannelActions } from '@/components/channels/disconnected-channel-actions';
import { DisableForwardingDialog } from '@/components/disable-forwarding-dialog';
import { DisableRetryDialog } from '@/components/disable-retry-dialog';
import { ChannelHealthSection } from '@/components/channel-health-section';
import { RoleGate } from '@/components/auth/role-gate';
import { SwitchToCliDialog } from '@/components/switch-to-cli-dialog';
import { WebhookConfigDialog } from '@/components/webhook-config-dialog';
import { useWorkspace } from '@/contexts/workspace-context';
import { channelDisplayName } from '@/lib/channel-display';
import { workspaceUrl } from '@/lib/workspace-url';
import type { Channel } from '@/types/channel';

export interface ChannelCardShellProps {
  channel: Channel;
  title: string;
  // The per-type body fills the field grid between the header and the action row.
  body: ReactNode;
  // Per-type "send test" CTA inside the info tip (wa.me link for WA, ig.me for IG).
  testActionLabel?: string;
  testActionHref?: string | null;
  // Wiring + perms:
  userRole: 'admin' | 'member';
  onDisconnected?: () => void;
  // Reconnect-in-place (D3): shown only when the channel is Meta-disconnected.
  onReconnect: () => void;
  reconnecting: boolean;
  // Channel-state pass-through (lifted from `channel-detail.tsx` local state):
  currentForwardingEnabled: boolean;
  currentWebhookUrl: string | null;
  currentVerifyToken: string | null;
  currentQualityRating: string | null;
  consecutiveForwardFailures: number;
  // Action handlers (parent-owned):
  onRefreshAll: () => void;
  refreshingAll: boolean;
  onDownload: () => void;
  downloading: boolean;
  onCopyCredentials: () => void;
  copiedCredentials: boolean;
  onEnable: () => void;
  onDisable: () => void;
  toggling: boolean;
  // Meta-retry toggle (default on). Off = forwarder always 200s to Meta.
  currentMetaRetryDisabled: boolean;
  onEnableRetry: () => void;
  onDisableRetry: () => void;
  togglingRetry: boolean;
  retryDialogOpen: boolean;
  setRetryDialogOpen: (open: boolean) => void;
  // WhatsApp quality rating — rendered below the toggle rows (WA-only).
  onRefreshQuality?: () => void;
  refreshingQuality?: boolean;
  // Modal state (parent-owned):
  showUrlDialog: boolean;
  setShowUrlDialog: (open: boolean) => void;
  urlDialogMode: 'configure' | 'edit';
  setUrlDialogMode: (mode: 'configure' | 'edit') => void;
  onUrlConfigured: (webhookUrl: string, verifyToken: string) => void;
  disableDialogOpen: boolean;
  setDisableDialogOpen: (open: boolean) => void;
  switchToCliDialogOpen: boolean;
  setSwitchToCliDialogOpen: (open: boolean) => void;
  onSwitchToCli: () => void;
  switchingToCli: boolean;
  showVideoModal: boolean;
  setShowVideoModal: (open: boolean) => void;
}

export function ChannelCardShell({
  channel,
  title,
  body,
  testActionLabel,
  testActionHref,
  userRole,
  onDisconnected,
  onReconnect,
  reconnecting,
  currentForwardingEnabled,
  currentWebhookUrl,
  currentVerifyToken,
  currentQualityRating,
  consecutiveForwardFailures,
  onRefreshAll,
  refreshingAll,
  onDownload,
  downloading,
  onCopyCredentials,
  copiedCredentials,
  onEnable,
  onDisable,
  toggling,
  currentMetaRetryDisabled,
  onEnableRetry,
  onDisableRetry,
  togglingRetry,
  retryDialogOpen,
  setRetryDialogOpen,
  onRefreshQuality,
  refreshingQuality,
  showUrlDialog,
  setShowUrlDialog,
  urlDialogMode,
  setUrlDialogMode,
  onUrlConfigured,
  disableDialogOpen,
  setDisableDialogOpen,
  switchToCliDialogOpen,
  setSwitchToCliDialogOpen,
  onSwitchToCli,
  switchingToCli,
  showVideoModal,
  setShowVideoModal,
}: ChannelCardShellProps) {
  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspace();
  const canEdit = userRole === 'admin';
  // The disconnected surface fires on EITHER a customer Disconnect
  // (channelStatus='disconnected', lifecycle-owned) OR Meta probe health loss
  // (!metaConnected). Lifecycle is the primary signal per the channel-disconnect
  // spec; a user-disconnected channel keeps metaConnected as a stale probe badge,
  // so keying the reconnect/Danger-Zone surfaces off metaConnected alone would
  // hide Reconnect for a lifecycle-disconnected channel.
  const isDisconnected =
    channel.channelStatus === 'disconnected' || !channel.metaConnected;

  const cliAlertDismissKey = `hmp:cli-alert-dismissed:${channel.id}`;
  const [cliAlertDismissed, setCliAlertDismissed] = useState(() => {
    try {
      return localStorage.getItem(cliAlertDismissKey) === '1';
    } catch {
      return false;
    }
  });

  function dismissCliAlert() {
    setCliAlertDismissed(true);
    try {
      localStorage.setItem(cliAlertDismissKey, '1');
    } catch {
      // localStorage unavailable (private mode, quota) — UI dismiss still works for the session
    }
  }

  const firstMessageAlertDismissKey = `hmp:firstmsg-alert-dismissed:${channel.id}`;
  const [firstMessageAlertDismissed, setFirstMessageAlertDismissed] = useState(() => {
    try {
      return localStorage.getItem(firstMessageAlertDismissKey) === '1';
    } catch {
      return false;
    }
  });

  function dismissFirstMessageAlert() {
    setFirstMessageAlertDismissed(true);
    try {
      localStorage.setItem(firstMessageAlertDismissKey, '1');
    } catch {
      // localStorage unavailable — UI dismiss still works for the session
    }
  }

  const messagingDisabledAlertDismissKey = `hmp:msgdisabled-alert-dismissed:${channel.id}`;
  const [messagingDisabledAlertDismissed, setMessagingDisabledAlertDismissed] = useState(() => {
    try {
      return localStorage.getItem(messagingDisabledAlertDismissKey) === '1';
    } catch {
      return false;
    }
  });

  function dismissMessagingDisabledAlert() {
    setMessagingDisabledAlertDismissed(true);
    try {
      localStorage.setItem(messagingDisabledAlertDismissKey, '1');
    } catch {
      // localStorage unavailable — UI dismiss still works for the session
    }
  }

  const qualityAlertDismissKey = `hmp:quality-alert-dismissed:${channel.id}`;
  const [qualityAlertDismissed, setQualityAlertDismissed] = useState(() => {
    try {
      return localStorage.getItem(qualityAlertDismissKey) === '1';
    } catch {
      return false;
    }
  });

  function dismissQualityAlert() {
    setQualityAlertDismissed(true);
    try {
      localStorage.setItem(qualityAlertDismissKey, '1');
    } catch {
      // localStorage unavailable — UI dismiss still works for the session
    }
  }

  function handleSetUrl() {
    setUrlDialogMode('configure');
    setShowUrlDialog(true);
  }

  return (
    <>
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Alerts — all warnings/errors at the top */}
        {!currentForwardingEnabled && !isDisconnected && !messagingDisabledAlertDismissed && (
          <Alert className="relative border-neutral-400/30 pr-10">
            <Pause className="size-4" />
            <AlertTitle>Messaging is disabled</AlertTitle>
            <AlertDescription>
              Messaging is paused for this channel. Enable it to resume sending and receiving.
            </AlertDescription>
            <button
              type="button"
              onClick={dismissMessagingDisabledAlert}
              aria-label="Dismiss"
              className="absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <X className="size-4" />
            </button>
          </Alert>
        )}

        {isDisconnected && canEdit && (
          <DisconnectedChannelActions
            channel={channel}
            onReconnect={onReconnect}
            reconnecting={reconnecting}
          />
        )}

        {!currentWebhookUrl && canEdit && !isDisconnected && !cliAlertDismissed && (
          <Alert className="relative border-blue-500/30 bg-blue-500/5 pr-10">
            <Info className="size-4 text-blue-500" />
            <AlertDescription className="space-y-3">
              <p>
                Inbound events will be delivered to the HookMyApp CLI.
                Configure a custom webhook URL to send them somewhere else.
              </p>
              <Button size="sm" onClick={handleSetUrl}>
                Configure Webhook
              </Button>
            </AlertDescription>
            <button
              type="button"
              onClick={dismissCliAlert}
              aria-label="Dismiss"
              className="absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-blue-500/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
            >
              <X className="size-4" />
            </button>
          </Alert>
        )}

        {consecutiveForwardFailures > 0 && (
          <Alert variant="destructive">
            <TriangleAlert className="size-4" />
            <AlertTitle>Webhook endpoint failing</AlertTitle>
            <AlertDescription>
              Your webhook endpoint has failed {consecutiveForwardFailures}{' '}
              consecutive time{consecutiveForwardFailures !== 1 ? 's' : ''}.
              Inbound events are not being delivered to your endpoint. Check that it is reachable
              and returning 200 responses.
            </AlertDescription>
          </Alert>
        )}

        {currentQualityRating === 'YELLOW' && !qualityAlertDismissed && (
          <Alert className="relative border-amber-500/30 pr-10">
            <TriangleAlert className="size-4 text-amber-500" />
            <AlertTitle>Medium quality rating</AlertTitle>
            <AlertDescription>
              Meta quality rating is medium and recipients may be reporting or blocking messages.
              Review your message templates and sending patterns.
            </AlertDescription>
            <button
              type="button"
              onClick={dismissQualityAlert}
              aria-label="Dismiss"
              className="absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <X className="size-4" />
            </button>
          </Alert>
        )}

        {currentQualityRating === 'RED' && (
          <Alert variant="destructive">
            <TriangleAlert className="size-4" />
            <AlertTitle>Low quality rating</AlertTitle>
            <AlertDescription>
              Meta quality rating is low. Recipients are reporting or blocking messages.
              This may affect your messaging limits. Review your messaging practices in
              Meta Business Suite.
            </AlertDescription>
          </Alert>
        )}

        {/* Info tip — only show when webhook is configured */}
        {currentWebhookUrl && !firstMessageAlertDismissed && (
          <Alert className="relative border-blue-500/30 bg-blue-500/5 pr-10">
            <Info className="size-4 text-blue-500" />
            <AlertDescription className="text-sm text-muted-foreground">
              To send and receive messages freely, you need to send a message from your own account to the channel you just connected.
            </AlertDescription>
            <div className="flex gap-2 mt-2">
              {testActionHref && testActionLabel && (
                <Button
                  size="sm"
                  className="w-fit"
                  onClick={() => window.open(testActionHref, '_blank')}
                >
                  <MessageCircle className="size-3.5" />
                  {testActionLabel}
                </Button>
              )}
              {channel.type === 'whatsapp' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  onClick={() => setShowVideoModal(true)}
                >
                  Watch How It Works
                </Button>
              )}
            </div>
            <button
              type="button"
              onClick={dismissFirstMessageAlert}
              aria-label="Dismiss"
              className="absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-blue-500/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
            >
              <X className="size-4" />
            </button>
          </Alert>
        )}

        {/* Channel Info Card */}
        <Card>
          <CardContent className="space-y-4">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-0">
              <div className="space-y-1">
                <h1 className="text-xl font-semibold">{title}</h1>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    activeWorkspace &&
                    navigate(
                      workspaceUrl(
                        activeWorkspace.id,
                        `channels/${channel.id}/logs`,
                      ),
                    )
                  }
                >
                  <ScrollText className="size-3.5" />
                  View Logs
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onCopyCredentials}
                  disabled={copiedCredentials}
                >
                  {copiedCredentials ? (
                    <Check className="size-3.5" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                  {copiedCredentials ? 'Copied!' : 'Copy All'}
                </Button>
                {!channel.metaConnected && (
                  <Badge variant="destructive">Meta Disconnected</Badge>
                )}
              </div>
            </div>

            {body}

            {/* Toggle rows — rendered in the same 200px/value grid as the field
                rows so the switches line up under the field values. Retry
                Messages first, then Forwarding (moved out of the header).
                Turning either off opens a confirmation dialog. */}
            {canEdit && (
              <div className="mt-2 grid grid-cols-1 md:grid-cols-[200px_1fr] items-center gap-x-10 gap-y-4 border-t pt-4 text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  Retry Messages
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <button
                          type="button"
                          aria-label="What does Retry Messages mean?"
                          className="inline-flex items-center justify-center p-0 leading-none text-muted-foreground/60 hover:text-muted-foreground"
                        />
                      }
                    >
                      <Info className="size-3.5" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      When a message can&rsquo;t reach your app, we keep trying
                      to deliver it so it isn&rsquo;t lost. Turn this off and a
                      message that fails to arrive the first time won&rsquo;t be
                      tried again.
                    </TooltipContent>
                  </Tooltip>
                </span>
                <Switch
                  checked={!currentMetaRetryDisabled}
                  aria-label="Toggle Retry Messages for this channel"
                  disabled={togglingRetry}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onEnableRetry();
                    } else {
                      setRetryDialogOpen(true);
                    }
                  }}
                />

                {!isDisconnected && (
                  <>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      Messaging
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <button
                              type="button"
                              aria-label="What does Messaging mean?"
                              className="inline-flex items-center justify-center p-0 leading-none text-muted-foreground/60 hover:text-muted-foreground"
                            />
                          }
                        >
                          <Info className="size-3.5" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          When on, this channel can send and receive.
                          Turn it off to pause both — messages that arrive
                          while it&rsquo;s off are not saved.
                        </TooltipContent>
                      </Tooltip>
                    </span>
                    <Switch
                      checked={currentForwardingEnabled}
                      aria-label="Toggle Messaging for this channel"
                      disabled={toggling}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onEnable();
                        } else {
                          setDisableDialogOpen(true);
                        }
                      }}
                    />
                  </>
                )}
              </div>
            )}

            {/* WhatsApp quality rating — sits below the Retry/Forward toggle
                rows per the card order. WA-only (IG has no quality rating). */}
            {channel.type === 'whatsapp' && (
              <ChannelHealthSection
                channel={channel}
                qualityRating={currentQualityRating}
                onRefreshQuality={onRefreshQuality}
                refreshingQuality={refreshingQuality}
              />
            )}
          </CardContent>
        </Card>

        {/* Actions bar */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onDownload}
            disabled={downloading}
          >
            {downloading ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            Download Credentials
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={onRefreshAll}
            disabled={refreshingAll}
          >
            <RefreshCw
              className={`size-3.5 ${refreshingAll ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        </div>

        {/* Danger Zone keys off channelStatus (ownership), NOT the Meta probe
            badge. A lifecycle-disconnected channel offers Reconnect only (no
            Remove). A badge-disconnected channel still owns its number, so it
            must keep Disconnect or the number can never be re-onboarded in
            another org (AIT-575). */}
        {channel.channelStatus !== 'disconnected' && (
          <RoleGate role="admin">
            <DangerZone
              channel={channel}
              onDisconnected={onDisconnected ?? (() => {})}
            />
          </RoleGate>
        )}
      </div>

      <WebhookConfigDialog
        open={showUrlDialog}
        onOpenChange={setShowUrlDialog}
        onConfigured={onUrlConfigured}
        mode={urlDialogMode}
        currentWebhookUrl={currentWebhookUrl || undefined}
        currentVerifyToken={currentVerifyToken || undefined}
        submitConfig={{
          endpoint: urlDialogMode === 'edit' ? `/webhook-config/${channel.id}` : '/webhook-config',
          method: urlDialogMode === 'edit' ? 'PUT' : 'POST',
          extraBody: urlDialogMode === 'configure' ? { channelId: channel.id } : undefined,
        }}
      />

      <DisableForwardingDialog
        channelName={channelDisplayName(channel)}
        confirmToken={
          channel.type === 'whatsapp'
            ? (channel.whatsappDisplayPhoneNumber ?? channelDisplayName(channel))
            : channelDisplayName(channel)
        }
        open={disableDialogOpen}
        onOpenChange={setDisableDialogOpen}
        onConfirm={onDisable}
        loading={toggling}
      />
      <DisableRetryDialog
        open={retryDialogOpen}
        onOpenChange={setRetryDialogOpen}
        onConfirm={onDisableRetry}
        loading={togglingRetry}
      />
      <SwitchToCliDialog
        open={switchToCliDialogOpen}
        onOpenChange={setSwitchToCliDialogOpen}
        onConfirm={onSwitchToCli}
        loading={switchingToCli}
      />
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>How it works</DialogTitle>
            <DialogDescription>Video explaining how messaging works on HookMyApp</DialogDescription>
          </DialogHeader>
          <video
            src="/how-it-works.mp4"
            autoPlay
            loop
            playsInline
            controls
            className="w-full"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
